import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { SimPhase, VexuCallerExperienceShowcase } from '../../components/deck/VexuCallerExperienceShowcase';
import { experienceModes } from '../../data/deckSlide5';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
const T = {
  headline: 0.2,
  support: 1.0,
  phone: 1.3,
  modes: 2.1,
  microcopy: 3.0
};
export function CallerExperienceSlide() {
  const [runKey, setRunKey] = useState(0);
  const [phase, setPhase] = useState<SimPhase>('idle');

  // The simulation holds on the final overview, then loops.
  const restart = () => {
    const id = window.setTimeout(() => setRunKey(key => key + 1), 4000);
    return () => window.clearTimeout(id);
  };
  const cleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanupRef.current?.(), []);
  return <SlideStage data-id="element-2159" data-mp="20bpwd1rz9qb7">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2160" data-mp="20b7unpmjqjvw">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 90% 70% at 8% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), radial-gradient(ellipse 55% 60% at 74% 45%, rgba(0,200,83,0.09) 0%, rgba(250,252,249,0) 68%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 55%, #EFF3ED 100%)'
      }} data-id="element-2161" data-mp="252zkfxxvolkz" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2162" data-mp="2o098jse5en8k">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2163" data-mp="2e58tejdi06w9" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2164" data-mp="ylcihx1m1vya">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2165" data-mp="35amtsch19qo8">
          05 · Caller Experience
        </motion.span>

        {/* LEFT — the claim */}
        <h1 className="absolute text-[44px] font-semibold leading-[1.18] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 176,
        width: 500
      }} data-id="element-2166" data-mp="2qk5tiwg4x6sx">
          {['Different callers.', 'Different experiences.', 'Your rules.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: T.headline + i * 0.16,
          ease
        }} data-id="element-2167" data-mp="2cobmkiyx8rig" data-mpi={i}>
                {line}
              </motion.span>)}
        </h1>

        <motion.p className="absolute text-[18px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 356,
        width: 440
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: T.support,
        ease
      }} data-id="element-2168" data-mp="ec0866lonwl5">
          VEXU adapts how it answers, acts and escalates based on who is
          calling.
        </motion.p>

        <div className="absolute" style={{
        left: 112,
        top: 476,
        width: 440
      }} data-id="element-2169" data-mp="3rkwc0bu77l9y">
          {experienceModes.map((mode, i) => {
          const active = phase === mode.id || phase === 'voice' && mode.id === 'family';
          return <motion.div key={mode.group} className="border-t border-[#0B1712]/[0.09] py-5 pl-4 -ml-4 rounded-r-xl transition-colors duration-500" style={{
            backgroundColor: active ? 'rgba(0,200,83,0.07)' : 'rgba(0,200,83,0)'
          }} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: T.modes + i * 0.2,
            ease
          }} data-id="element-2170" data-mp="9df6s3up36v4" data-mpi={i}>
                <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] transition-colors duration-500 ${active ? 'text-vexu-700' : 'text-vexu-700/70'}`} data-id="element-2171" data-mp="32s1wthiigxuc">
                  {mode.group}
                </p>
                <p className={`mt-2 text-[17px] transition-colors duration-500 ${active ? 'font-medium text-[#0B1712]' : 'text-[#33463B]'}`} data-id="element-2172" data-mp="37ptlm2nv03ub">
                  {mode.behaviour}
                </p>
              </motion.div>;
        })}
        </div>

        {/* MIDDLE — the statement, and the replay control */}
        <motion.div className="absolute border-l pl-6 transition-colors duration-700" style={{
        left: 700,
        top: 448,
        width: 290,
        borderColor: phase === 'done' ? 'rgba(0,153,64,0.55)' : 'rgba(0,153,64,0.25)'
      }} initial={{
        opacity: 0,
        x: -6
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 1,
        delay: T.microcopy,
        ease
      }} data-id="element-2173" data-mp="3vffl6r858l2u">
          <p className={`text-[22px] leading-[1.35] tracking-[-0.01em] transition-all duration-700 ${phase === 'done' ? 'font-semibold text-[#0B1712]' : 'font-medium text-[#33463B]'}`} data-id="element-2174" data-mp="8vjabneh1a80">
            One number.
            <br data-id="element-2175" data-mp="3hd7fnrn72xyf" />
            Different AI behavior.
          </p>

          <motion.p className="mt-5 text-[14.5px] leading-relaxed text-[#5A6B60]" initial={{
          opacity: 0
        }} animate={{
          opacity: ['voice', 'vip', 'done'].includes(phase) ? 1 : 0
        }} transition={{
          duration: 0.8,
          ease
        }} data-id="element-2176" data-mp="3hf0mp81pzk6y">
            Your AI can sound like you — or your brand.
          </motion.p>

        </motion.div>

        {/* RIGHT — the real product, running live */}
        <motion.div className="absolute" style={{
        left: 1060,
        top: 84
      }} initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: T.phone,
        ease
      }} data-id="element-2177" data-mp="1e4dr1zvuxche">
          <VexuCallerExperienceShowcase scale={0.86} runKey={runKey} onPhaseChange={setPhase} onComplete={() => {
          cleanupRef.current?.();
          cleanupRef.current = restart();
        }} data-id="element-2178" data-mp="144eiw43cno52" />
        </motion.div>
      </div>
    </SlideStage>;
}